import type { HaveFinish } from 'utils/finishes';
import type { AddCardToHaveListResponse } from './haveListApi';

/**
 * How the Have List cache absorbs a finish change (Asana 1219423900684919).
 *
 * The backend answers PATCH /have-list/card/update with the surviving row. Two
 * outcomes exist:
 *   - plain change: the edited row keeps its place and takes the response's
 *     id, finish, quantity and price (the backend writes a new row, so the id
 *     moves; no refetch, which would re-sort the list);
 *   - merge: a row of the target finish already existed, so the edited row is
 *     folded into it. The backend says so with `merged`, `mergedIntoId` and
 *     `removedId`; an older backend only shows it as `id` landing on a row
 *     already in the cache, which is kept as the fallback.
 */
export interface SettleResult<T> {
  items: T[];
  /** false when the survivor is not in this cache slice (another page): refetch. */
  converged: boolean;
}

type Row = { id: number; [key: string]: unknown };

export const settleFinishChange = <T extends Row>(
  items: T[],
  haveListRecordId: number,
  finish: HaveFinish,
  res: AddCardToHaveListResponse,
): SettleResult<T> => {
  const editedIndex = items.findIndex((item) => item.id === haveListRecordId);
  if (editedIndex === -1) return { items, converged: true };

  const fields = {
    finish: res.finish ?? finish,
    foil: res.foil ?? finish !== 'NONFOIL',
    quantity: res.quantity,
    displayPrice: res.displayPrice ?? null,
    // The response carries no priceKey; clearing it drops a marker that
    // belonged to the previous finish rather than showing a stale one.
    priceKey: undefined,
  };

  const survivorId = res.mergedIntoId ?? res.id;
  const removedId = res.removedId ?? haveListRecordId;
  const survivorIndex = items.findIndex((item) => item.id === survivorId && item.id !== removedId);

  if (survivorIndex !== -1 && (res.merged === true || survivorId !== haveListRecordId)) {
    const next = items.map((item, i) => (i === survivorIndex ? { ...item, ...fields } : item));
    const removedIndex = next.findIndex((item) => item.id === removedId);
    if (removedIndex !== -1) next.splice(removedIndex, 1);
    return { items: next, converged: true };
  }
  if (res.merged === true) {
    // Merged into a row this slice does not hold; drop the edited row and let
    // the caller refetch so the survivor's quantity shows.
    return { items: items.filter((item) => item.id !== removedId), converged: false };
  }
  return {
    items: items.map((item, i) => (i === editedIndex ? { ...item, ...fields, id: res.id } : item)),
    converged: true,
  };
};

/**
 * Whether a finish-change response means the row was merged into an existing
 * copy: the backend's `merged` flag when present, else (older backend) the id
 * moving onto a row that was already on screen before the call.
 */
export const wasFinishMerge = (
  res: AddCardToHaveListResponse,
  haveListRecordId: number,
  knownIds: ReadonlySet<number>,
): boolean => {
  if (typeof res.merged === 'boolean') return res.merged;
  return res.id !== haveListRecordId && knownIds.has(res.id);
};
